// @ts-check
import React, { Component } from 'react';
import api from 'utils/api';
import { PlainSelectField, TextField, SelectField } from 'shared/containers/portal_layout/HForm';
import { reduxForm } from 'utils/reduxFormWrapper';
import { InjectedFormProps } from 'redux-form'; // eslint-disable-line no-unused-vars
import { required } from 'utils/validation';
import SpinnerButton from 'shared/components/SpinnerButton';
import Errors from 'shared/components/Errors';
import { timeZoneOptions } from 'lib/TimeZones';

function validateZipLength(value) {
  if (value && value.length !== 5) {
    return 'Must be 5 digits';
  }
  return null;
}

/** @typedef {{
  organization: {
    name: string
  },
  location: {
    name: string,
    timeZoneName: string,
    physicalAddressAttributes: {
      streetAddress: string,
      streetAddressLine2?: string,
      city: string,
      stateId: string,
      zip: string
    }
  },
  providerId: string
}} LocationFormData */

/** @typedef {{
  createLocation: (formValues: LocationFormData) => Promise,
  onSuccess: () => void,
  providerId: string
 } & InjectedFormProps<LocationFormData> } CreateLocationFormPropsType */

/** @typedef {{
  id: string,
  name: string
}} State */

/** @typedef {{
  states?: State[]
}} ComponentState */

class _CreateLocationForm extends Component {
  constructor(props) {
    super(props);
    this.state = { states: null };
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    return api.get('/shared/api/states.json').then(resp => this.setState({ states: resp.data }));
  }

  submit(formValues) {
    const { createLocation, onSuccess } = this.props;
    return createLocation(formValues).then(onSuccess);
  }

  render() {
    const { handleSubmit, errors, pristine, submitting } = this.props;
    const { states } = this.state;

    return (
      <div>
        <form className="form-horizontal" onSubmit={handleSubmit(this.submit)}>
          <header className="modal-card-head">
            <p className="modal-card-title">Enter Referring Practice</p>
            <button className="delete" aria-label="close" />
          </header>
          <section className="modal-card-body">
            <p style={{ marginBottom: '1rem' }}>
              If you couldn't find the referring practice in the search dropdown, you can enter it
              here. After entering, the practice will be available in the search dropdown for future
              referrals.
            </p>
            <Errors errors={errors || []} />
            <TextField
              id="referringPracticeName"
              name="organization.name"
              label="Practice Name"
              validate={required}
            />
            <TextField
              id="referringLocationName"
              name="location.name"
              label="Location Name"
              validate={required}
            />
            <TextField
              id="referringLocationAddress"
              name="location.physicalAddressAttributes.streetAddress"
              label="Street Address"
              validate={required}
            />
            <TextField
              id="referringLocationAddressLine2"
              name="location.physicalAddressAttributes.streetAddressLine2"
              label="Street Address Line 2"
            />
            <TextField
              id="referringLocationCity"
              name="location.physicalAddressAttributes.city"
              label="City"
            />
            <SelectField
              name="location.physicalAddressAttributes.stateId"
              isLoading={!states}
              options={states}
              valueKey="id"
              labelKey="name"
              label="State"
              validate={required}
            />
            <TextField
              id="referringLocationZip"
              name="location.physicalAddressAttributes.zip"
              label="Zip"
              validate={[required, validateZipLength]}
            />
            <PlainSelectField
              name="location.timeZoneName"
              label="Timezone"
              options={timeZoneOptions}
              validate={required}
            />
            <footer className="modal-card-foot">
              <SpinnerButton
                type="button"
                onClick={handleSubmit(this.submit)}
                className="button is-primary is-medium"
                loading={submitting}
                disabled={submitting || pristine}
              >
                Enter Practice Location
              </SpinnerButton>
            </footer>
          </section>
        </form>
      </div>
    );
  }
}

const CreateLocationForm = reduxForm({
  form: 'createLocationForm',
})(_CreateLocationForm);

export { CreateLocationForm };
